import {Row, Col, Button, Divider} from "antd";
import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {FormProvider, useForm, Controller} from "react-hook-form";
import FieldBuilder from "../../form-components/field-builder";
import MainForm from "../../form-components/main-form";
import Spin from "../../general/antd/spin";
import UnitPartitionContext from "../../../context/unit-partition/context";
import UnitPartitionContextProvider from "../../../context/unit-partition/provider";
import {IUpdateUnitPartition} from "../../../models/unit-partition/request";
import {getTranslations} from "../../../utils/helpers/translations";
import {languages} from "../../../utils/helpers/constants";
import {execute} from "../../../utils/helpers/execute";
import styles from "./style.module.scss";
import {IUnit} from "../../../models/unit/response";
import EndPoints from "../../../services/end-points";
import AppContext from "../../../context/app/context";

const Form = () => {
    const {t} = useTranslation();
    const {details, actions, loading} = useContext(UnitPartitionContext);
    const {id} = useParams();
    const navigate = useNavigate();
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const {direction} = useContext(AppContext);

    const [units, setUnits] = useState<IUnit[]>([]);
    const [unitsLoading, setUnitsLoading] = useState(true);

    useEffect(() => {
        const getDetails = async () => {
            id && (await actions.getDetails(Number(id)));
        };
        getDetails();
    }, []);
    useEffect(() => {
        const getUnits = async () => {
            try {
                const {data} = await EndPoints.unit.getAllUnits({
                    light: true,
                    perPage: -1,
                });
                setUnits(data?.data);
            } catch (error) {
            } finally {
                setUnitsLoading(false);
            }
        };
        getUnits();
    }, [direction]);

    const methods = useForm<any>({
        defaultValues: {
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            percentage: details?.percentage,
            unitId: details?.unit?.id,
        } as IUpdateUnitPartition,
    });

    const {
        handleSubmit,
        reset,
        control,
        formState: {errors},
    } = methods;

    useEffect(() => {
        reset({
            name: getTranslations(details?.translations?.name),
            note: getTranslations(details?.translations?.note),
            percentage: details?.percentage,
            unitId: details?.unit?.id,
        } as IUpdateUnitPartition);
    }, [details]);


    const onSubmit = async (data: any) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateUnitPartition(details?.id, data)
                    : await actions.createUnitPartition(data);
                navigate(-1);
            },
            fallback: (error) => {
                // Handle error
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} style={{padding: "20px"}} onSubmit={handleSubmit(onSubmit)}>
                <div>
          <span className={styles.title}>
            {details ? t("update_unit_partition") : t("add_unit_partition")}
          </span>
                    <Divider className={styles.divider}/>
                    {loading.includes("details") ? (
                        <div style={{height: "60vh"}}>
                            <Spin/>
                        </div>
                    ) : (
                        <div>
                            <Row gutter={[16, 8]}>
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`name[${lang?.code}]`}
                                            label={`${t("name")} (${lang?.name})`}
                                            input={{type: "text"}}
                                            rules={{required: true}}
                                        />
                                    </Col>
                                ))}
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`note[${lang?.code}]`}
                                            label={`${t("note")} (${lang?.name})`}
                                            input={{type: "text-area"}}
                                            rules={{required: false}}
                                        />
                                    </Col>
                                ))}
                                <Col span={12}>
                                    <FieldBuilder
                                        name="percentage"
                                        label={t("percentage")}
                                        input={{type: "number"}}
                                        rules={{required: true}}
                                    />
                                </Col>
                                <Col span={12}>
                                    <FieldBuilder
                                        name="unitId"
                                        label={`${t("unit")}`}
                                        width="large"
                                        rules={{required: true}}
                                        input={{
                                            type: "select",
                                            allowSearch: true,
                                            loading: unitsLoading,
                                            options: units?.map((unit, index) => {
                                                return {
                                                    key: index,
                                                    label: unit.name,
                                                    value: unit.id,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
                <div>
                    <Divider className={styles.divider}/>
                    <div className={styles.formFooter}>
                        <Button
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const UnitPartitionForm = () => {
    return (
        <UnitPartitionContextProvider>
            <Form/>
        </UnitPartitionContextProvider>
    );
};

export default UnitPartitionForm;